export const SIZE_BUCKET_DEFINITIONS = [
  { id: 'less_than_250', label: 'Less than 250', min: 0, max: 249, radiusMultiplier: 0.8, priority: 0, order: 5 },
  { id: '250_to_499', label: '250 to 499', min: 250, max: 499, radiusMultiplier: 1.0, priority: 1, order: 4 },
  { id: '500_to_999', label: '500 to 999', min: 500, max: 999, radiusMultiplier: 1.35, priority: 2, order: 3 },
  { id: '1000_to_4999', label: '1,000 to 4,999', min: 1000, max: 4999, radiusMultiplier: 1.9, priority: 3, order: 2 },
  { id: '5000_to_19999', label: '5,000 to 19,999', min: 5000, max: 19999, radiusMultiplier: 2.8, priority: 4, order: 1 },
  { id: '20000_or_more', label: '20,000 or more', min: 20000, max: null, radiusMultiplier: 4.0, priority: 5, order: 0 },
] as const;

export const UNKNOWN_SIZE_BUCKET = {
  id: 'unknown',
  label: 'Unknown',
  radiusMultiplier: 0.7,
  priority: -1,
  order: 6,
} as const;

export const NOT_PROVIDED_SIZE_BUCKET = {
  id: 'not_provided',
  label: 'Not Provided',
  order: 7,
} as const;

export type SizeBucket = (typeof SIZE_BUCKET_DEFINITIONS)[number] | typeof UNKNOWN_SIZE_BUCKET;
export type SizeBucketId = (typeof SIZE_BUCKET_DEFINITIONS)[number]['id'] | typeof UNKNOWN_SIZE_BUCKET.id;

export const SIZE_BUCKETS_BY_ID = new Map<SizeBucketId, SizeBucket>([
  ...SIZE_BUCKET_DEFINITIONS.map((bucket) => [bucket.id, bucket] as const),
  [UNKNOWN_SIZE_BUCKET.id, UNKNOWN_SIZE_BUCKET],
]);

const normalizeLabel = (value: string) => value.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ').trim();

const SIZE_LABEL_TO_CANONICAL_ENTRIES: Array<[string, string]> = [
  ...SIZE_BUCKET_DEFINITIONS.map((bucket): [string, string] => [normalizeLabel(bucket.label), bucket.label]),
  [normalizeLabel(UNKNOWN_SIZE_BUCKET.label), UNKNOWN_SIZE_BUCKET.label],
  [normalizeLabel(NOT_PROVIDED_SIZE_BUCKET.label), NOT_PROVIDED_SIZE_BUCKET.label],
];

const SIZE_LABEL_ORDER_LOOKUP_ENTRIES: Array<[string, number]> = [
  ...SIZE_BUCKET_DEFINITIONS.map((bucket): [string, number] => [normalizeLabel(bucket.label), bucket.order]),
  [normalizeLabel(UNKNOWN_SIZE_BUCKET.label), UNKNOWN_SIZE_BUCKET.order],
  [normalizeLabel(NOT_PROVIDED_SIZE_BUCKET.label), NOT_PROVIDED_SIZE_BUCKET.order],
];

const SIZE_LABEL_TO_CANONICAL = new Map<string, string>(SIZE_LABEL_TO_CANONICAL_ENTRIES);
const SIZE_LABEL_ORDER_LOOKUP = new Map<string, number>(SIZE_LABEL_ORDER_LOOKUP_ENTRIES);

export const normalizeSizeRangeForSort = (value: string) => normalizeLabel(value);

export const canonicalizeSizeRange = (value?: string | null): string => {
  if (!value) return UNKNOWN_SIZE_BUCKET.label;
  const normalized = normalizeLabel(String(value).replace(/\bemployees?\b/g, ''));
  const mapped = SIZE_LABEL_TO_CANONICAL.get(normalized);
  if (mapped) return mapped;
  return UNKNOWN_SIZE_BUCKET.label;
};

export const getSizeRangeOrder = (value: string): number =>
  SIZE_LABEL_ORDER_LOOKUP.get(normalizeLabel(value)) ?? Number.MAX_SAFE_INTEGER;
